import { Injectable } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { ContextualInfo } from '@cds/betting-offer/domain-specific';
import { BetBuilderMarket, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { CustomerOfferService } from '@frontend/sports/new-customer-offer-feature';
import { IPickTracking } from '@frontend/sports/option/pick/feature/tracking';

import { BABAdditionalInfo, BetBuilderV1EventData } from '../../../core/external-betslip-actions';
import { BetslipGroupInformation } from '../../../core/groups/betslip-group-information';
import { BetslipBetBuilderPick, SportcastOption } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import {
    BetBuilderPickId,
    PickId,
    V1PickId,
    V2OptionMarketPickId,
    V2OptionMarketXCastPickId,
    V2ParticipantPickId,
    V2WinPickId,
} from '../../../core/picks/pick-id';
import OptionPickFactory from './option-pick.factory';
import ParticipantPickFactory from './participant-pick.factory';
import V1PickFactory from './v1-pick.factory';

@Injectable()
export default class BetslipPickFactory {
    private highlightedSourcesOfBets = ['rebet'];

    constructor(
        private v1PickFactory: V1PickFactory,
        private participantPickFactory: ParticipantPickFactory,
        private optionPickFactory: OptionPickFactory,

        private customerOfferService: CustomerOfferService,
    ) {}

    //TODO consider refactoring, replace multiple params with an object.
    createPick(
        pickId: V1PickId | V2OptionMarketPickId | V2ParticipantPickId | V2OptionMarketXCastPickId,
        sourceOfBet: IPickTracking,
        fixture: Fixture,
        priceId?: number,
        isPriceBoosted?: boolean,
        contextualInfo?: ContextualInfo,
        groupInfo?: BetslipGroupInformation | null,
    ): BetslipPick | null {
        let pick: BetslipPick | null = null;
        if (pickId instanceof V1PickId) {
            pick = this.v1PickFactory.create(pickId, fixture, isPriceBoosted);
        } else if (pickId instanceof V2OptionMarketPickId) {
            pick = this.optionPickFactory.createOptionMarketPick(pickId, fixture, isPriceBoosted);
        } else if (pickId instanceof V2WinPickId) {
            pick = this.participantPickFactory.createWinPick(pickId, fixture, priceId);
        } else if (pickId instanceof V2OptionMarketXCastPickId) {
            pick = this.optionPickFactory.createOptionMarketXCastPick(pickId, fixture);
        } else {
            pick = this.participantPickFactory.createXCastPick(pickId, fixture);
        }

        if (pick) {
            pick.highlighted = this.isPickHighlighted(sourceOfBet);
        }

        if (!!pick && !!contextualInfo) {
            pick.contextualInfo = this.mapContextualInfo(pickId, contextualInfo);
        }

        if (!!pick && !!groupInfo) {
            pick.setGroupInfo(groupInfo);
        }

        return this.updatePick(pick, fixture, sourceOfBet);
    }

    createBetBuilderPick(
        pickId: BetBuilderPickId,
        sourceOfBet: IPickTracking,
        fixture: Fixture,
        eventData: BetBuilderV1EventData | null,
        contextualInfo?: ContextualInfo,
        additionalInfo?: BABAdditionalInfo,
    ): BetslipPick | null {
        const pick = this.optionPickFactory.createBetBuilderPick(pickId, fixture, eventData);

        if (!!pick && !!contextualInfo) {
            pick.contextualInfo = this.mapContextualInfo(pickId, contextualInfo);
        }

        if (!!pick && !!additionalInfo) {
            pick.sportcastId = additionalInfo.sportcastId;
            pick.betgeniusId = additionalInfo.betgeniusId;
            pick.longIds = additionalInfo.longIds;
            pick.useV2Key = additionalInfo.useV2Key;
        }

        return this.updatePick(pick, fixture, sourceOfBet);
    }

    createEntainUiBetBuilderPick(
        pickId: BetBuilderPickId,
        event: BetBuilderV1EventData,
        eventModel: EventModel,
        optionMarket: BetBuilderMarket,
        tracking: IPickTracking,
        sportcastOptions: SportcastOption[],
    ): BetslipBetBuilderPick {
        const pick = this.optionPickFactory.createEntainUiBetBuilderPick(pickId, event, eventModel, optionMarket, sportcastOptions);
        if (pick) {
            pick.tracking = tracking;
            pick.liveAlert = eventModel.liveAlert;
        }

        return pick;
    }

    convertPreCreatedBABPick(pick: BetslipBetBuilderPick): BetslipBetBuilderPick {
        const newpick = this.optionPickFactory.convertPreCreatedBABPick(pick);

        return newpick;
    }

    /**
     * Creates a new pick by keeping the price from previous version of this pick ( used for page refreshes and main to live switch )
     *
     * @param oldPick = previous version of the pick
     * @param pickId = pick id
     * @param fixture = latest version of the fixture
     * @returns updated pick.
     */
    createNewPickFromOldAndBcdPick(oldPick: BetslipPick, pickId: PickId, fixture: Fixture): BetslipPick | null {
        // create new pick id that uses the mappings

        const newPick = BetslipBetBuilderPick.isPick(oldPick)
            ? this.createBetBuilderPickFromPreviousVersion(oldPick, fixture)
            : this.createPick(
                  pickId as V1PickId | V2OptionMarketPickId | V2ParticipantPickId,
                  oldPick.tracking,
                  fixture,
                  oldPick.currentPrice?.id,
                  undefined,
                  undefined,
                  oldPick.groupInfo,
              );
        if (!newPick) {
            return null;
        }
        newPick.highlighted = false;
        // Set price type, in order to keep if user selected SP/Fixed price
        // Although that we pass priceId, we need this if the new resolved pick is a live one and has different price ids for fixed/sp
        // When the new pick is from the same fixture then the priceId will be the same and this will the the same.
        newPick.priceType = oldPick.priceType;
        // Set accepted price to accepted one from pre-match
        newPick.acceptedPrice = { ...oldPick.acceptedPrice };
        // push in price history the pre-match price.
        newPick.priceHistory.push(...oldPick.priceHistory);

        newPick.boostedPriceDetails = oldPick.boostedPriceDetails && { ...oldPick.boostedPriceDetails };

        return newPick;
    }

    private isPickHighlighted(sourceOfBet: IPickTracking): boolean {
        return this.highlightedSourcesOfBets.indexOf(sourceOfBet.source) > -1;
    }

    private createBetBuilderPickFromPreviousVersion(oldPick: BetslipBetBuilderPick, fixture: Fixture): BetslipPick | null {
        const additionalInfo: BABAdditionalInfo = {
            betgeniusId: oldPick.betgeniusId,
            longIds: oldPick.longIds,
            sportcastId: oldPick.sportcastId,
            useV2Key: oldPick.useV2Key,
        };
        const newBetBuilderPick = this.createBetBuilderPick(oldPick.id, oldPick.tracking, fixture, null, undefined, additionalInfo);
        if (newBetBuilderPick) {
            // Move the event data from the old pick to the new one.
            (newBetBuilderPick as unknown as BetslipBetBuilderPick).event = oldPick.event;
        }

        return newBetBuilderPick;
    }

    private updatePick(pick: BetslipPick | null, fixture: Fixture, sourceOfBet: IPickTracking): BetslipPick | null {
        if (pick) {
            pick.subscriptionContext = fixture.contexts;
            pick.tracking = sourceOfBet;
            pick.liveAlert = fixture.liveAlert;
            const leagueId = fixture.competition?.id ?? fixture.meeting?.id;

            pick.isNewCustomerOfferPick = this.customerOfferService.isNewCustomerOffer(leagueId);
        }

        return pick;
    }

    private mapContextualInfo(pickId: PickId, contextualInfo: ContextualInfo | undefined): string | undefined {
        if (pickId instanceof V1PickId) {
            return contextualInfo?.resultsContextualInfo[pickId.optionId];
        }

        if (pickId instanceof V2OptionMarketPickId) {
            return contextualInfo?.optionsContextualInfo[pickId.optionId];
        }

        return undefined;
    }
}
